"use client";
import React, { useState, useEffect, useRef } from "react";

type NavItem = {
  label: string;
  title: string;
  href: string;
  isActive?: boolean;
  className?: string;
};

type Props = {
  navMenu: NavItem[];
};

const Navigation = ({ navMenu }: Props) => {
  const [activeId, setActiveId] = useState<string>("");

  const observer = useRef<IntersectionObserver | null>(null);

  useEffect(() => {
    const handleStickyMenu = () => {
      document.body.classList.toggle("nav-sticky", window.scrollY >= 80);
    };

    window.addEventListener("scroll", handleStickyMenu);

    const sections = navMenu.map((item) =>
      document.querySelector(item.href)
    ) as HTMLElement[];

    if (observer.current) observer.current.disconnect();

    observer.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveId(`#${entry.target.id}`);
          }
        });
      },
      {
        rootMargin: "0px 0px -60% 0px",
        threshold: 0.1,
      }
    );

    sections.forEach((section) => {
      if (section) observer.current?.observe(section);
    });

    return () => {
      window.removeEventListener("scroll", handleStickyMenu);
      observer.current?.disconnect();
    };
  }, [navMenu]);

  return (
    <nav
      className={`sticky top-[90px] md:top-[64px] z-10 bg-white py-3 transition-all border-t border-gray-3 ease-in-out duration-300 ${
        typeof window !== "undefined" && window.scrollY >= 80 ? "shadow pt-10" : ""
      }`}
    >
      <div className="max-w-[1170px] mx-auto px-4 sm:px-8 xl:px-0 flex justify-center gap-4 text-sm font-medium text-gray-700">
        {navMenu.map((item, idx) => (
          <a
            key={idx}
            href={item.href}
            title={item.title}
            className={`transition hover:text-purple-600 ${
              activeId === item.href ? "text-purple-600 font-semibold" : "text-gray-600"
            } ${item.className || ""}`}
          >
            {item.label}
          </a>
        ))}
      </div>
    </nav>
  );
};

export default Navigation;
